import { TextField, SxProps } from '@mui/material';
import { FC, ChangeEvent, ReactNode, useState, useEffect } from 'react';
import { useForm, FormData } from '@carcareers/contexts';

export interface TextInputProps {
  label: string;
  name: string;
  required?: boolean;
  fullWidth?: boolean;
  autoComplete?: string;
  endAdornment?: ReactNode;
  multiline?: boolean;
  rows?: number;
  type?: 'text' | 'number' | 'password' | 'email' | 'currency';
  disabled?: boolean;
  sx?: SxProps;
}

// Helper function to format currency with commas
const formatCurrency = (value: string): string => {
  // Remove all non-numeric characters except decimal point
  const numericValue = value.replace(/[^0-9.]/g, '');
  
  // Handle empty input
  if (numericValue === '') {
    return '';
  }
  
  // Handle multiple decimal points - keep only the first one
  const parts = numericValue.split('.');
  if (parts.length > 2) {
    const formattedValue = parts[0] + '.' + parts.slice(1).join('');
    return formatCurrency(formattedValue);
  }
  
  // If there's a decimal point, format the integer part and preserve decimals
  if (parts.length === 2) {
    const integerPart = parts[0];
    const decimalPart = parts[1].slice(0, 2); // Limit to 2 decimal places
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    
    // Return with decimal point and whatever decimals they've typed
    return `${formattedInteger}.${decimalPart}`;
  }
  
  // No decimal point, just format with commas
  return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// Helper function to format currency for display (adds trailing zeros for complete decimal places)
const formatCurrencyForDisplay = (value: string): string => {
  const formatted = formatCurrency(value);
  
  // If it has a decimal point, ensure it has exactly 2 decimal places
  if (formatted.includes('.')) {
    const parts = formatted.split('.');
    const decimalPart = parts[1];
    
    if (decimalPart.length === 1) {
      return `${parts[0]}.${decimalPart}0`;
    } else if (decimalPart.length === 0) {
      return `${parts[0]}.00`;
    }
    return formatted;
  }
  
  // For whole numbers, add .00 for complete formatting
  return `${formatted}.00`;
};

// Helper function to parse currency back to number
const parseCurrency = (value: string): number | undefined => {
  const numericValue = value.replace(/[^0-9.]/g, '');
  if (numericValue === '' || numericValue === '.') {
    return undefined;
  }
  const parsed = parseFloat(numericValue);
  return isNaN(parsed) ? undefined : parsed;
};

export const TextInput: FC<TextInputProps> = ({
  label,
  name,
  required,
  fullWidth,
  autoComplete,
  endAdornment,
  sx,
  type = 'text',
  multiline = false,
  rows = 1,
  disabled = false,
}) => {
  const { setData, getData, getErrors } = useForm<FormData>();
  const [currencyDisplayValue, setCurrencyDisplayValue] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);

  // Update display value when form data changes (for currency inputs)
  useEffect(() => {
    if (type === 'currency' && !isFocused) {
      const value = getData(name);
      if (value !== undefined && value !== null) {
        setCurrencyDisplayValue(formatCurrencyForDisplay(String(value)));
      } else {
        setCurrencyDisplayValue('');
      }
    }
  }, [getData(name), type, name, isFocused]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    if (type === 'number') {
      // Allow empty string for number inputs
      if (inputValue === '') {
        setData(name, undefined);
      } else {
        const numericValue = Number(inputValue);
        // Only set numeric value if it's a valid number
        setData(name, isNaN(numericValue) ? undefined : numericValue);
      }
    } else if (type === 'currency') {
      // Handle currency input - format as they type but don't auto-pad
      const formattedValue = formatCurrency(inputValue);
      const numericValue = parseCurrency(formattedValue);
      
      setCurrencyDisplayValue(formattedValue);
      setData(name, numericValue);
    } else {
      setData(name, inputValue);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (type === 'currency') {
      const value = getData(name);
      if (value !== undefined && value !== null) {
        // Show the value without auto-padding when focused
        setCurrencyDisplayValue(formatCurrency(String(value)));
      }
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (type === 'currency') {
      // On blur, format with proper decimal places
      const currentValue = getData(name);
      if (currentValue !== undefined && currentValue !== null) {
        setCurrencyDisplayValue(formatCurrencyForDisplay(String(currentValue)));
      }
    } else {
      setData(name, getData(name));
    }
  };

  const getValue = () => {
    const value = getData(name);

    if (type === 'number') {
      // Return empty string if value is undefined/null, otherwise return the value as string
      return value !== undefined && value !== null ? String(value) : '';
    }

    if (type === 'currency') {
      // Return the display value we're tracking
      return currencyDisplayValue;
    }

    return value ?? '';
  };

  return (
    <TextField
      label={label}
      required={required}
      value={getValue()}
      fullWidth={fullWidth}
      autoComplete={autoComplete}
      type={type === 'currency' ? 'text' : type}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      error={!!getErrors(name)}
      helperText={getErrors(name)}
      multiline={multiline}
      rows={rows}
      disabled={disabled}
      slotProps={{
        input: {
          sx,
          endAdornment: endAdornment,
        },
      }}
    />
  );
};

export default TextInput;
